import { Download, Eye, FileText, Loader, RefreshCw } from "lucide-react";
import { useState } from "react";

import { FilePreviewDialog, type PreviewFile } from "@/components/files/file-preview-dialog";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { notify } from "@/lib/notify";

import type { ClaimWithRelations } from "../../api/use-claim";
import { downloadFromBoxFileId, useGenerateAssignationWithStampedPieces } from "../../api/use-claim-actions";
import { toPreviewFile } from "../claim-documents";
import { ExhibitsList } from "../exhibits/exhibits-list";
import { useClaimExhibits } from "../exhibits/use-claim-exhibits";

/** Port of states-cta/MergeAssignationCTA.vue — bundle assignation + pièces tamponnées. */
export function MergeAssignationCTA({ claim }: { claim: ClaimWithRelations }) {
  const generateAssignation = useGenerateAssignationWithStampedPieces(claim.id);
  const [isDownloading, setIsDownloading] = useState(false);
  const [previewedAssignation, setPreviewedAssignation] = useState<PreviewFile | null>(null);

  const {
    exhibits,
    exhibitData,
    isFetching: isFetchingExhibits,
    previewedFile,
    previewDocument,
    clearPreview,
  } = useClaimExhibits(claim.id);

  const significationFile = claim.legalSubpoena?.pdfFile ?? null;
  const significationBoxFileId = significationFile?.boxFileId ?? null;

  async function generate() {
    // Prepare ordered exhibits to send to backend
    const orderedExhibits = exhibits.map((doc) => ({
      boxFileId: doc.boxFileId,
      name: doc.name,
      exhibitNumber: doc.exhibitNumber,
      ...(doc.exhibitSubNumber !== undefined && { exhibitSubNumber: doc.exhibitSubNumber }),
    }));

    const result = await generateAssignation.mutateAsync({ orderedExhibits });
    // Auto-download
    await downloadFromBoxFileId(result.boxFileId, result.filename);
  }

  async function downloadAssignation() {
    if (!significationBoxFileId || !significationFile) {
      return;
    }
    setIsDownloading(true);
    try {
      await downloadFromBoxFileId(significationBoxFileId, significationFile.name ?? "Assignation.pdf");
    } catch (error) {
      notify.error("Erreur lors du téléchargement", {
        description: error instanceof Error ? error.message : "Une erreur est survenue",
      });
    } finally {
      setIsDownloading(false);
    }
  }

  return (
    <div className="border-b bg-slate-50">
      <FilePreviewDialog open={Boolean(previewedFile)} file={previewedFile} onClose={clearPreview} />
      <FilePreviewDialog
        open={previewedAssignation !== null}
        file={previewedAssignation}
        onClose={() => setPreviewedAssignation(null)}
      />

      <div className="mx-auto max-w-xl py-5 text-left">
        <div className="rounded-md border bg-white p-6 shadow">
          <div className="mb-4 flex items-center gap-3">
            <div className="rounded-full bg-purple-100 p-2">
              <FileText className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <h2 className="text-base leading-6 font-semibold text-gray-900">
                Fusionner l'assignation et les pièces
              </h2>
              <p className="text-sm text-gray-500">
                Bundle complet à transmettre à l'huissier (assignation + pièces tamponnées)
              </p>
            </div>
          </div>

          {/* Exhibits listing (same as GenerateStampedExhibitsCTA) */}
          {isFetchingExhibits ? (
            <div className="flex items-center justify-center py-8">
              <Spinner className="size-6 text-gray-400" />
            </div>
          ) : (
            <ExhibitsList className="mb-4" exhibitData={exhibitData} onPreview={previewDocument} />
          )}

          {significationBoxFileId ? (
            <>
              <div className="mb-4 rounded-md border border-green-200 bg-green-50 p-4">
                <p className="text-sm text-green-700">L'assignation a déjà été générée.</p>
              </div>
              <div className="flex justify-center gap-2">
                {significationFile ? (
                  <Button
                    variant="outline"
                    onClick={() => setPreviewedAssignation(toPreviewFile(significationFile))}
                  >
                    <Eye />
                    Aperçu
                  </Button>
                ) : null}
                <Button onClick={downloadAssignation} disabled={isDownloading}>
                  {isDownloading ? <Loader className="animate-spin" /> : <Download />}
                  Télécharger
                </Button>
                <Button variant="outline" onClick={generate} disabled={generateAssignation.isPending}>
                  {generateAssignation.isPending ? <Loader className="animate-spin" /> : <RefreshCw />}
                  Régénérer
                </Button>
              </div>
            </>
          ) : (
            <div className="mt-4 text-center">
              <Button
                onClick={generate}
                disabled={
                  generateAssignation.isPending || exhibitData.toStamp.length === 0 || isFetchingExhibits
                }
              >
                {generateAssignation.isPending ? <Loader className="animate-spin" /> : <FileText />}
                Fusionner l'assignation et les pièces
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
